import { Link } from 'react-router-dom';
import { ROUTES } from '@traction/shared';
import { ArrowRight, Sparkles, TrendingUp, Users, Zap, CheckCircle, Coffee, GraduationCap, Building2, Target } from 'lucide-react';

export function HomePage() {
  return (
    <div className="bg-cacao-dark">
      {/* Hero Section - Punchy and Clear */}
      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 rounded-full bg-cacao-gold/10 border border-cacao-gold/20 px-4 py-1.5 text-xs font-medium text-cacao-gold mb-6">
            <Sparkles className="h-3.5 w-3.5" />
            For pre-revenue founders who hate cold outreach
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
            <span className="text-[#F5F5F5]">Stop spending </span>
            <span className="text-cacao-gold">2 hours</span>
            <span className="text-[#F5F5F5]"> on outreach</span>
            <br />
            <span className="text-[#F5F5F5]">Start spending </span>
            <span className="text-cacao-gold">2 minutes</span>
          </h1>

          <p className="mt-6 text-lg sm:text-xl text-cacao-muted max-w-2xl mx-auto leading-relaxed">
            Wake up to <span className="text-[#F5F5F5] font-medium">3 high-intent leads</span> with
            <span className="text-[#F5F5F5] font-medium"> hyper-personalized emails</span> ready to send.
            AI finds the prospects. AI writes the emails. You just hit send.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to={ROUTES.SIGNUP}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg bg-cacao-gold px-8 py-3.5 text-base font-semibold text-cacao-dark shadow-lg shadow-cacao-gold/25 hover:bg-cacao-gold-hover hover:shadow-cacao-gold/40 transition-all"
            >
              Start free trial
              <ArrowRight className="h-5 w-5" />
            </Link>
            <p className="text-sm text-cacao-text-tertiary">
              14 days free • No credit card • Cancel anytime
            </p>
          </div>

          {/* Quick social proof */}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-6 sm:gap-8 text-sm text-cacao-muted">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-400" />
              <span><span className="text-[#F5F5F5] font-medium">15%</span> avg reply rate</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-400" />
              <span><span className="text-[#F5F5F5] font-medium">3x</span> industry average</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-400" />
              <span>Works in <span className="text-[#F5F5F5] font-medium">Gmail</span></span>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="border-t border-cacao-dark-tertiary py-16 bg-cacao-dark-secondary/50">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#F5F5F5]">
              You didn't become a founder to do cold outreach
            </h2>
            <p className="mt-4 text-cacao-muted max-w-2xl mx-auto">
              Yet here you are, spending hours researching leads, crafting personalized emails,
              and hoping someone replies. <span className="text-[#F5F5F5]">There's a better way.</span>
            </p>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-2">
            <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-6">
              <p className="text-xs font-semibold text-red-400 uppercase tracking-wide mb-4">The old way</p>
              <ul className="space-y-3 text-sm text-cacao-muted">
                <li className="flex items-start gap-3">
                  <span className="text-red-400 mt-0.5">✕</span>
                  <span>2+ hours daily on LinkedIn research</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400 mt-0.5">✕</span>
                  <span>Generic templates that get ignored (3% reply rate)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400 mt-0.5">✕</span>
                  <span>$3K-8K/month for an SDR agency</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400 mt-0.5">✕</span>
                  <span>Inconsistent outreach when you're building product</span>
                </li>
              </ul>
            </div>
            <div className="rounded-xl border border-cacao-gold/30 bg-cacao-gold/5 p-6">
              <p className="text-xs font-semibold text-cacao-gold uppercase tracking-wide mb-4">The Traction Bee way</p>
              <ul className="space-y-3 text-sm text-cacao-muted">
                <li className="flex items-start gap-3">
                  <span className="text-cacao-gold mt-0.5">✓</span>
                  <span>2 minutes to review and send 3 leads</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cacao-gold mt-0.5">✓</span>
                  <span>AI-personalized emails that actually get replies (15%+)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cacao-gold mt-0.5">✓</span>
                  <span>$49/month — 10x cheaper than agencies</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cacao-gold mt-0.5">✓</span>
                  <span>Pipeline keeps growing even when you're heads-down</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="border-t border-cacao-dark-tertiary py-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-cacao-gold uppercase tracking-wide">How it works</p>
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-[#F5F5F5]">
              From zero to sent in 2 minutes
            </h2>
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            <div className="relative text-center">
              <div className="absolute left-1/2 top-6 hidden h-0.5 w-full bg-gradient-to-r from-cacao-gold/50 to-cacao-gold/20 md:block" />
              <div className="relative mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-cacao-gold text-cacao-dark font-bold">
                1
              </div>
              <h3 className="mt-4 text-base font-semibold text-[#F5F5F5]">Scout finds high-intent leads</h3>
              <p className="mt-2 text-sm text-cacao-muted">
                AI identifies prospects with <span className="text-cacao-gold">buying signals</span> — funding rounds, hiring sprees, expansion moves. Not just database keywords.
              </p>
            </div>
            <div className="relative text-center">
              <div className="absolute left-1/2 top-6 hidden h-0.5 w-full bg-gradient-to-r from-cacao-gold/20 to-transparent md:block" />
              <div className="relative mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-cacao-gold text-cacao-dark font-bold">
                2
              </div>
              <h3 className="mt-4 text-base font-semibold text-[#F5F5F5]">Scribe writes hyper-personalized emails</h3>
              <p className="mt-2 text-sm text-cacao-muted">
                AI researches each lead and writes emails <span className="text-cacao-gold">in your voice</span>. References their funding, shared connections, and common ground.
              </p>
            </div>
            <div className="text-center">
              <div className="relative mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-cacao-gold text-cacao-dark font-bold">
                3
              </div>
              <h3 className="mt-4 text-base font-semibold text-[#F5F5F5]">Review and send from Gmail</h3>
              <p className="mt-2 text-sm text-cacao-muted">
                One-click opens Gmail with your email pre-filled. <span className="text-cacao-gold">Edit if you want, or just hit send.</span> Takes 30 seconds per lead.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="border-t border-cacao-dark-tertiary py-14">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 md:grid-cols-3">
            <div className="rounded-xl border border-cacao-dark-tertiary bg-cacao-dark-secondary p-6 text-center">
              <div className="text-3xl font-bold text-cacao-gold">15%</div>
              <p className="mt-1 text-sm text-cacao-muted">Avg reply rate</p>
              <p className="mt-2 text-xs text-cacao-text-tertiary">3x industry average</p>
            </div>
            <div className="rounded-xl border border-cacao-dark-tertiary bg-cacao-dark-secondary p-6 text-center">
              <div className="text-3xl font-bold text-cacao-gold">2 min</div>
              <p className="mt-1 text-sm text-cacao-muted">Daily time investment</p>
              <p className="mt-2 text-xs text-cacao-text-tertiary">vs 2+ hours manual</p>
            </div>
            <div className="rounded-xl border border-cacao-dark-tertiary bg-cacao-dark-secondary p-6 text-center">
              <div className="text-3xl font-bold text-cacao-gold">3</div>
              <p className="mt-1 text-sm text-cacao-muted">Warm leads daily</p>
              <p className="mt-2 text-xs text-cacao-text-tertiary">With buying signals</p>
            </div>
          </div>
        </div>
      </section>

      {/* Secret Sauce Section - Shared Connections */}
      <section className="border-t border-cacao-dark-tertiary py-16 bg-gradient-to-b from-cacao-dark to-cacao-dark-secondary/50">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-cacao-gold uppercase tracking-wide">The secret sauce</p>
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-[#F5F5F5]">
              Turn cold emails into warm introductions
            </h2>
            <p className="mt-4 text-cacao-muted max-w-2xl mx-auto">
              Traction Bee automatically finds shared connections between you and every lead —
              same schools, past employers, cities, industries. Then weaves them into your emails naturally.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-cacao-dark-tertiary bg-cacao-dark p-5 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-500/10">
                <GraduationCap className="h-6 w-6 text-green-400" />
              </div>
              <h3 className="mt-3 text-sm font-semibold text-[#F5F5F5]">Alumni Networks</h3>
              <p className="mt-1 text-xs text-cacao-muted">
                "We both went through Stanford CS..."
              </p>
            </div>
            <div className="rounded-xl border border-cacao-dark-tertiary bg-cacao-dark p-5 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-500/10">
                <Building2 className="h-6 w-6 text-blue-400" />
              </div>
              <h3 className="mt-3 text-sm font-semibold text-[#F5F5F5]">Past Employers</h3>
              <p className="mt-1 text-xs text-cacao-muted">
                "Ex-Stripe folks unite..."
              </p>
            </div>
            <div className="rounded-xl border border-cacao-dark-tertiary bg-cacao-dark p-5 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/10">
                <Coffee className="h-6 w-6 text-amber-400" />
              </div>
              <h3 className="mt-3 text-sm font-semibold text-[#F5F5F5]">Same City</h3>
              <p className="mt-1 text-xs text-cacao-muted">
                "Coffee in the Marina?"
              </p>
            </div>
            <div className="rounded-xl border border-cacao-dark-tertiary bg-cacao-dark p-5 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-purple-500/10">
                <Target className="h-6 w-6 text-purple-400" />
              </div>
              <h3 className="mt-3 text-sm font-semibold text-[#F5F5F5]">Same Industry</h3>
              <p className="mt-1 text-xs text-cacao-muted">
                "Fellow SaaS founders..."
              </p>
            </div>
          </div>

          <div className="mt-10 rounded-xl border border-cacao-gold/20 bg-cacao-gold/5 p-6 text-center">
            <p className="text-lg font-medium text-[#F5F5F5]">
              Cold emails with common ground get <span className="text-cacao-gold">3-5x higher response rates</span>
            </p>
            <p className="mt-2 text-sm text-cacao-muted">
              Because you're not a stranger anymore — you're a connection.
            </p>
          </div>
        </div>
      </section>

      {/* Why Traction Bee Section */}
      <section className="border-t border-cacao-dark-tertiary py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#F5F5F5]">
              Built different
            </h2>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-xl border border-cacao-dark-tertiary bg-cacao-dark-secondary p-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-500/10">
                <Zap className="h-5 w-5 text-green-400" />
              </div>
              <h3 className="mt-4 text-sm font-semibold text-[#F5F5F5]">Buying signals, not keywords</h3>
              <p className="mt-2 text-sm text-cacao-muted leading-relaxed">
                We find leads showing active buying signals — funding rounds, hiring sprees, expansion. Not just people who match job titles.
              </p>
            </div>
            <div className="rounded-xl border border-cacao-dark-tertiary bg-cacao-dark-secondary p-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500/10">
                <Users className="h-5 w-5 text-blue-400" />
              </div>
              <h3 className="mt-4 text-sm font-semibold text-[#F5F5F5]">Your voice, not a template</h3>
              <p className="mt-2 text-sm text-cacao-muted leading-relaxed">
                Edit one email, and Traction Bee learns your style. Every draft gets more natural. No more generic AI copy.
              </p>
            </div>
            <div className="rounded-xl border border-cacao-dark-tertiary bg-cacao-dark-secondary p-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/10">
                <TrendingUp className="h-5 w-5 text-amber-400" />
              </div>
              <h3 className="mt-4 text-sm font-semibold text-[#F5F5F5]">Works where you work</h3>
              <p className="mt-2 text-sm text-cacao-muted leading-relaxed">
                No new dashboard to check. Our Chrome extension lives in Gmail. Review, tweak, send. That's it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA Section */}
      <section className="border-t border-cacao-dark-tertiary bg-gradient-to-b from-cacao-dark-secondary to-cacao-dark py-20">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#F5F5F5]">
            Your competitors are still doing cold outreach the hard way
          </h2>
          <p className="mt-4 text-lg text-cacao-muted max-w-xl mx-auto">
            While they spend hours crafting generic emails, you could be sending hyper-personalized outreach in 2 minutes.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to={ROUTES.SIGNUP}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg bg-cacao-gold px-8 py-3.5 text-base font-semibold text-cacao-dark shadow-lg shadow-cacao-gold/25 hover:bg-cacao-gold-hover hover:shadow-cacao-gold/40 transition-all"
            >
              Start your free trial
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
          <p className="mt-4 text-sm text-cacao-text-tertiary">
            14 days free • No credit card required • Takes 5 minutes to set up
          </p>
        </div>
      </section>
    </div>
  );
}
